/* eslint-disable max-len */
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import AdminTable from '../../../../components/AdminTable';
import EmptyRecord from '../../../../components/EmptyRecord';
import useGetQueryParams from '../../../../customHooks/useGetQueryParams';
import useTableColumn from '../../../../customHooks/useTableColumn';
import useToast from '../../../../customHooks/useToast';
import { GET_EQUIFAX_REPORTS_ADMIN_DASHBOARD } from '../../../../graphql/admin_queries';
import useCustomQuery from '../../../../network/useCustomQuery';
import generateCSV from '../../../../utils/generateCSV';
import generatePDF from '../../../../utils/generatePdf';
import { adminHasPermission, cleanUpEquifaxReports } from '../../../../utils/helpers';
import { IEquifaxReportsTableRecords } from './EquifaxReportsTableRecords.types';
import RolesAndPermissionGuard from '../../../../components/RolesAndPermissionGuard';
import PermissionDenied from '../../../PermissionDenied';
import DashboardHeader from '../../../../components/DashboardHeader';
import AdminLinkTabs from '../../../../components/AdminLinkTabs';
import { useGlobalState } from '../../../../contexts';
import CustomerTabs from '../../../AdminCustomers/CustomerTabs';
import { AdminRouteName } from '../../../CONSTANTS';

import EmptyMerchantSettlement from '../../../../svgs/EmptyMerchantSettlement';
import EquifaxGenerateReport from '../EquifaxGenerateReport';

const limit = 14;
const EquifaxReportsTableRecords = ({
  defaultUrl, toggleStorageId, tabStatus, hasNameAndImage, permissionName, optionalColumn,
  optionalColumnLabelFormat,
}:IEquifaxReportsTableRecords) => {
  const { profile } = useGlobalState();
  const adminPermissions = profile?.role?.permissions || [];
  const Toast = useToast();
  const [getAllEquifaxReports, equifaxReportsLoading, equifaxReports] = useCustomQuery({
    query: GET_EQUIFAX_REPORTS_ADMIN_DASHBOARD,
    onError: () => {},
    onData: () => {},
  });
  const { getQuery } = useGetQueryParams();
  const search = getQuery('search') || '';
  const page = Number(getQuery('page'));
  const { push } = useHistory();

  const allColumns = [
    ['image', 'email', 'hasBottomValue', 'name'],
    'fileName',
    'equifaxSubmittedAt',
    ...(optionalColumn || []),
    'month',
    'submissionStatus',
    'btnType',
  ];

  const columnLabelFormat = {
    name: 'Submitted by',
    fileName: 'File name',
    equifaxSubmittedAt: 'Date Submitted',
    ...(optionalColumnLabelFormat || {}),
    month: 'Month',
    submissionStatus: 'Status',
    btnType: ' ',
  };

  const { getColumns, applyChanges, selectedColumns } = useTableColumn(allColumns, toggleStorageId);

  const [equifaxReportsRec, setequifaxReportsRec] = useState(null);
  const [modalDetail, setModalDetail] = useState(null);

  const setPage = (pageNum) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', pageNum);
    push({
      search: searchParams.toString(),
    });
  };
  if (!page) {
    push(`${defaultUrl}?page=1&search=${search}`);
  }

  const onSearchChange = (e) => {
    push(`${defaultUrl}?page=1&search=${encodeURIComponent(e.target.value)}`);
  };

  const equifaxReportData = equifaxReportsRec?.data;

  const TabsRaw = [
    {
      id: 1,
      name: 'All Reports',
      link: AdminRouteName.equifaxReportRoute,
      permission: 'can-update-rent-payment-settlement-status',
      count: equifaxReportData?.count || 0,
    },
    {
      id: 2,
      name: 'Not submitted reports',
      link: AdminRouteName.notSubmittedEquifaxReportRoute,
      permission: 'can-update-rent-payment-settlement-status',
      count: equifaxReportData?.count || 0,
    },
    {
      id: 3,
      name: 'Submitted Reports',
      link: AdminRouteName.equifaxSubmittedReportRoute,
      permission: 'can-update-rent-payment-settlement-status',
      count: equifaxReportData?.count || 0,
    },
  ];

  const Tabs = TabsRaw?.map((tab) => ({
    ...tab,
    disabled: !adminHasPermission(adminPermissions, tab.permission),
  }));

  useEffect(() => {
    if (page) {
      getAllEquifaxReports({
        variables: {
          offset: (page - 1) * limit,
          limit,
          search,
          ...(tabStatus ? { statusFilter: tabStatus } : {}),
        },
      });
    }
  }, [page, search]);

  useEffect(() => {
    if (equifaxReports?.getAllEquifaxReports?.data) {
      setequifaxReportsRec(equifaxReports?.getAllEquifaxReports);
    }
  }, [equifaxReports]);

  const [getExportRecord] = useCustomQuery({
    query: GET_EQUIFAX_REPORTS_ADMIN_DASHBOARD,
    onError: () => {},
    onData: () => {},
  });

  const handleExport = async (type: string) => {
    try {
      const output = await getExportRecord({
        variables: {
          offset: 0,
          limit: 2000,
          search,
          statusFilter: tabStatus,
        },
      });
      if (output?.data?.getAllEquifaxReports?.data?.equifaxReports?.rows) {
        const rec = cleanUpEquifaxReports(output?.data?.getAllEquifaxReports?.data?.equifaxReports?.rows);
        const func = type === 'pdf' ? generatePDF : generateCSV;
        const selectedHeaders = [
          'S/N',
          // change image__email__hasBottomValue__name concated columns to a single column of name
          ...(getColumns?.()?.map((column) => (column === 'image__email__hasBottomValue__name' ? ['name', 'email'] : column)) || []),
        ];

        func({
          payload: rec,
          title: 'Rewards',
          selectedHeaders,
          formatHeaders: columnLabelFormat,
          cb: () => Toast.success('Exported successfully'),
        });
      }
    } catch (error) {
      Toast.error('Error while exporting');
    }
  };

  const handleRoute = (data:any) => {
    console.log(data?.equifaxSubmitted, 'data?.equifaxSubmitted >>>>');
    if (!data?.equifaxSubmitted) {
      setModalDetail({
        title: 'Mark as Submitted to Equifax',
        subTitle: 'You are about to mark this rent payment as submitted to Equifax',
        action: 'markEquifaxFileAsSubmitted',
        actionText: 'Update File',
        id: data?.id,
      });
    } else {
      setModalDetail({
        title: 'Mark as Submitted to Equifax',
        subTitle: 'This file has already been submitted to equifax',
        action: 'downloadEquifaxFile',
        actionText: 'Download File',
        id: data?.id,
      });
    }
  };

  const refetchQuery = () => {
    getAllEquifaxReports({
      variables: {
        offset: (page - 1) * limit,
        limit,
        search,
        ...(tabStatus ? { statusFilter: tabStatus } : {}),
      },
    });
  };

  return (
    <div>
      <EquifaxGenerateReport
        refetch={refetchQuery}
        setModalDetail={setModalDetail}
        modalDetail={modalDetail}
      />
      <DashboardHeader
        searchPlaceholder="Name / Month"
        search={search}
        onSearchChange={onSearchChange}
        searchLoading={equifaxReportsLoading}
        columnObject={{
          allColumns,
          formatLabels: columnLabelFormat,
          applyChanges,
          selectedColumns,
        }}
        disableSearch={false}
        disableExport={false}
        disbleFilter={false}
        exportObj={{
          handleExport,
        }}
      >
        <AdminLinkTabs>
          <CustomerTabs Tabs={Tabs} />
        </AdminLinkTabs>

      </DashboardHeader>

      <RolesAndPermissionGuard permissionName={permissionName} hasErrorTemplate={<PermissionDenied height="60vh" />}>
        <div className="transaction-table-details-cover">
          {equifaxReportData?.count ? (
            <AdminTable
              refreshing={equifaxReportsLoading}
              page={page}
              setPage={setPage}
              pageSize={limit}
              minHeight="calc(100vh - 280px)"
              count={equifaxReportData?.count}
              contents={cleanUpEquifaxReports(equifaxReportData?.rows)}
              reference="id"
              hideDominantImage
              clickable
              handleClick={handleRoute}
              hasNameAndImage={hasNameAndImage}
              allowKeyArr={getColumns(true)}
              mobileAllowKeyArr={
                [
                  'status',
                  ['name', 'email'],
                  ['location', 'earnings'],
                ]
              }
              formatLabels={columnLabelFormat}
              // btnObj={{
              //   text: () => 'Download file',
              //   showOn: (data: any) => {
              //     console.log(data);
              //     return true;
              //   },
              //   cb: () => {

              //   },
              // }}
            />
          ) : (
            <EmptyRecord
              height="calc(100vh - 240px)"
              title={equifaxReportsLoading ? 'Fetching reports...' : 'No Reports yet'}
              main="All equifax reports will appear here"
              loading={equifaxReportsLoading}
              Icon={EmptyMerchantSettlement}
            />
          )}

        </div>
      </RolesAndPermissionGuard>

    </div>
  );
};

export default EquifaxReportsTableRecords;
